<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :color="'#000000'"
    minimized-width="0"
    class="flex flex-col h-full text-white pb-20 md:pb-0 bg-[#0C1020] border-r border-[#1F2436]"
  >
    <!-- Menu principal - apenas para desktop -->
    <div class="flex flex-col pt-6 px-4 space-y-2">
      <RouterLink
        v-for="item in menuItems"
        :key="item.route"
        :to="`/${item.route}`"
        class="group relative flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold tracking-wide transition-all duration-200 uppercase shadow-sm"
        :class="{
          'bg-orange-500 text-white shadow-lg': isActive(item.route),
          'bg-[#1C1F33] text-white/70 hover:text-orange-400 hover:shadow-md':
            !isActive(item.route),
        }"
      >
        <component :is="item.icon" class="w-5 h-5" />
        <span class="truncate">{{ $t(item.label) }}</span>
      </RouterLink>
      <button
        class="bg-[#1C1F33] text-white/70 hover:text-orange-400 hover:shadow-md group relative flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold tracking-wide transition-all duration-200 uppercase shadow-sm"
        @click="abrirPdfOnboarding"
      >
        <VaIcon name="picture_as_pdf" />
        Guia Empresarial
      </button>
    </div>

    <!-- Rodapé fixo -->
    <div class="px-3 pb-4 space-y-4 mt-auto">
      <div class="w-full pt-2 border-t border-[#1F2436]">
        <label class="block text-xs text-white/60 mb-1">Idioma</label>
        <VaSelect
          v-model="language"
          :options="[
            { label: '🇧🇷 Português', value: 'br' },
            { label: '🇺🇸 English', value: 'gb' },
            { label: '🇪🇸 Español', value: 'es' },
          ]"
          :track-by="'value'"
          :text-by="'label'"
          :value-by="'value'"
          :clearable="false"
          size="small"
          class="w-full custom-language-select"
          background="background-element"
          color="orange"
          text-color="white"
        />
      </div>
    </div>
  </VaSidebar>

  <OnboardingPdfModal
    v-model="showPdfModal"
    pdf-url="/apresentation.pdf"
    @close="fecharPdfOnboarding"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useGlobalStore } from "@/stores/global-store";
import { VaIcon, VaSelect, VaSidebar } from "vuestic-ui";
import { useI18n } from "vue-i18n";

import {
  AcademicCapIcon,
  ClockIcon,
  DocumentTextIcon,
  HomeIcon,
  LifebuoyIcon,
  UsersIcon,
  WalletIcon,
} from "@heroicons/vue/24/solid";
import OnboardingPdfModal from "@/components/modal/OnboardingPdfModal.vue";

const route = useRoute();
const { isSidebarMinimized } = storeToRefs(useGlobalStore());
const { t: $t, locale } = useI18n();

const showPdfModal = ref(false);

const language = computed({
  get: () => locale.value,
  set: (val) => (locale.value = val),
});

const props = defineProps({
  visible: { type: Boolean, default: true },
  mobile: { type: Boolean, default: false },
});

const fecharPdfOnboarding = () => {
  showPdfModal.value = false;
};

const writableVisible = computed({
  get: () => !isSidebarMinimized.value,
  set: (v: boolean) => {
    isSidebarMinimized.value = !v;
  },
});
const abrirPdfOnboarding = () => {
  showPdfModal.value = true;
};

const sidebarWidth = computed(() => (props.mobile ? "100vw" : "250px"));

const isActive = (routeName: string) => route.name === routeName;

const menuItems = [
  { route: "dashboard", label: "sidebar.dashboard", icon: HomeIcon },
  { route: "stake", label: "sidebar.cotas", icon: WalletIcon },
  { route: "network", label: "sidebar.network", icon: UsersIcon },
  { route: "operations", label: "sidebar.operations", icon: DocumentTextIcon },
  { route: "history", label: "sidebar.history", icon: ClockIcon },
  {
    route: "tutoriais",
    label: "sidebar.content_education",
    icon: AcademicCapIcon,
  },
  { route: "support", label: "sidebar.support", icon: LifebuoyIcon },
];
</script>

<style scoped>
:deep(.va-select__anchor) {
  background-color: #0a1121;
  border: 1px solid #1f2436;
  color: white;
}

.custom-language-select :deep(.va-select__anchor) {
  background-color: #1c1f33;
  border: 1px solid #2c334a;
  color: #ffffff;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
}
</style>
